import { ActionIcon, Group, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { type TodoNote } from "../../../../utils/backend-types";
import { type SiteName } from "../../../../utils/enums";
import { MarkdownLink } from "../../../ReactMarkdown/MarkdownLink";
import { SYSTEM_USER_NAME } from "../../Todos.constants";
import { type NoteMutation } from "./Notes.types";
import { NotesEntry } from "./NotesEntry";

declare const SITE_NAME: SiteName;

interface NoteProps {
  note: TodoNote;
  isStaffUser: boolean;
  ownUserId: number;
  onDeleteNote: () => void;
  onUpdateNote: NoteMutation;
  onUpdateNoteDone?: (note: TodoNote) => void;
}

function Note({
  note,
  isStaffUser,
  ownUserId,
  onUpdateNote,
  onUpdateNoteDone,
  onDeleteNote
}: NoteProps) {
  const [editing, setEditing] = useState(false);
  const toggleEditing = () => setEditing(!editing);

  const authorIsSystemUser = note.author.name === SYSTEM_USER_NAME;
  const authorIsCurrentUser = ownUserId === note.author.id;
  const author = authorIsSystemUser ? SITE_NAME : note.author.name;
  const canEdit = authorIsCurrentUser || (isStaffUser && authorIsSystemUser);
  const canDelete = authorIsCurrentUser || (isStaffUser && authorIsSystemUser);
  const showControls = canEdit || canDelete;

  function handleUpdateNoteDone(note: TodoNote) {
    if (onUpdateNoteDone) onUpdateNoteDone(note);
    setEditing(false);
  }

  return (
    <div className="Note" tabIndex={0}>
      {editing ? (
        <NotesEntry
          isStaffUser={isStaffUser}
          note={note}
          submitButtonText="Speichern"
          onCancel={toggleEditing}
          onSaveNote={onUpdateNote}
          onSaveNoteDone={handleUpdateNoteDone}
        />
      ) : (
        <>
          <Group className="info" gap="xs">
            <Text fw={500}>{author}</Text>|<Text c="#888">{note.created}</Text>
            {note.internal && (
              <>
                |<Text>Intern</Text>
              </>
            )}
            {showControls && (
              <>
                |
                <Tooltip label="Notiz bearbeiten">
                  <ActionIcon
                    aria-label="Notiz bearbeiten"
                    color="info"
                    size="xs"
                    variant="transparent"
                    onClick={toggleEditing}
                  >
                    <IconEdit />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Notiz löschen">
                  <ActionIcon
                    aria-label="Notiz löschen"
                    color="error"
                    size="xs"
                    variant="transparent"
                    onClick={onDeleteNote}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
          <div className="text">
            <ReactMarkdown
              components={{
                a: MarkdownLink
              }}
            >
              {note.note}
            </ReactMarkdown>
          </div>
        </>
      )}
    </div>
  );
}

export { Note };
